import { Injectable } from '@angular/core';
import { DocumentViewModel } from 'src/app/modules/shared/viewModels/documents/documentViewModel';
import { Observable, Observer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { UploadViewModel } from 'src/app/modules/shared/viewModels/documents/uploadViewModel';
import { Configuration } from 'src/app/app.constants';
import { AlertService } from './alert.service';
import { ObjectTypes } from '../enums/objectTypes';
import { NgxImageCompressService } from 'ngx-image-compress';
import { AuthenticationService } from './authentication.service';
import { WtStorageService } from './wt-storage.service';
import { CopiedAttachmentsWhenLinkObjectViewModel } from '../models/linkedItemObjects/multipleLinkedItemsObjectViewModel';
import { ChecklistDocumentViewModel } from '../models/checklists/checklistAttachmentsViewModel';
import { FileChunkingUtilities } from '../utilities/file-chunking.utilities';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {

  constructor(
    private readonly http: HttpClient,
    private readonly configuration: Configuration,
    private readonly alertService: AlertService,
    private readonly imageCompress: NgxImageCompressService,
    private readonly authenticationService: AuthenticationService,
    private readonly wtStorage: WtStorageService
  ) {
    
  }

  /**
   * Getter that dynamically builds the actionUrl each time.
   * This ensures the URL updates when the backend API changes (e.g., account switching).
   */
  private get actionUrl(): string {
    return this.configuration.buildEndpoint(`Documents/`);
  }

  addDocument(uploadViewModel: UploadViewModel): Observable<DocumentViewModel> {
    return this.http.post<DocumentViewModel>(this.actionUrl + 'AddDocument', uploadViewModel);
  }

  uploadFile(file: File, isAttachment: boolean = false, isAccountLogo: boolean = false): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<string>(`${this.actionUrl}UploadFile/${isAttachment.toString()}/${isAccountLogo.toString()}`, formData, {
      responseType: 'text' as 'json',
    });
  }

  uploadPart(part: number, originalFileName: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<void>(`${this.actionUrl}UploadPart/${part}/${originalFileName}`, formData);
  }

  completeUpload(
    originalFileName: string,
    newFileName: string,
    isAttachment: boolean,
    isAccountLogo: boolean
  ): Observable<string> {
    return this.http.post<string>(
      `${this.actionUrl}CompleteUpload/${originalFileName}/${newFileName}/${isAttachment.toString()}/${isAccountLogo.toString()}`,
      {},
      this.authenticationService.getHttpOptions()
    );
  }

  addDocuments(uploadViewModels: UploadViewModel[]): Observable<DocumentViewModel[]> {
    return this.http.post<DocumentViewModel[]>(this.actionUrl + 'AddMutipleDocuments', uploadViewModels);
  }

  deleteDocument(documentId: number, globalObjectId: number, globalObjectType: number): Observable<void> {
    return this.http.delete<void>(`${this.actionUrl}Delete/${documentId}/${globalObjectId}/${globalObjectType}`);
  }

  updateDocument(uploadViewModel: UploadViewModel): Observable<DocumentViewModel> {
    return this.http.put<DocumentViewModel>(this.actionUrl + 'Update', uploadViewModel);
  }

  getByObjectId(objectId: number, objectType: ObjectTypes): Observable<DocumentViewModel[]> {
    return this.http.get<DocumentViewModel[]>(this.actionUrl + `ByObjectId/${objectId}/${objectType}`);
  }

  downloadDocument(id: number) {
    let filename = '';
    const url = this.actionUrl + `Download/${id}`;
    const requestHeaders = new Headers();
    requestHeaders.append('Authorization', 'Bearer ' + this.wtStorage.getItem('access_token'));

    fetch(url, {
      method: 'GET',
      headers: requestHeaders,
      mode: 'cors',
    })
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.Message as string);
        }

        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(response.headers.get('Content-Disposition'));

        if (matches !== null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }

        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        this.forceDownload(blobUrl, filename);
      })
      .catch((err) => this.alertService.error(err.message as string));
  }

  private forceDownload(blob: string, filename: string) {
    const anchorElement = document.createElement('a');
    anchorElement.download = filename;
    anchorElement.href = blob;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    anchorElement.remove();
  }

  deleteUnassignedFile(fileName: string): Observable<void> {
    return this.http.delete<void>(`${this.actionUrl}DeleteUnassignedFile/${fileName}`);
  }

  splitUploadFile(file: File, compress: boolean = false, isAttachment = true, isAccountLogo = false): Observable<string> {
    if (compress) {
      return FileChunkingUtilities.splitAndUploadFile(
        file,
        (part, fileName, file) => this.uploadPart(part, fileName, file),
        (originalFileName, newFileName, isAttachment, isAccountLogo) =>
          this.completeUpload(originalFileName, newFileName, isAttachment, isAccountLogo),
        isAttachment,
        isAccountLogo,
        (fileName, base64Data, isAttachment, isAccountLogo) =>
          this.compressAndSaveImage(fileName, base64Data, isAttachment, isAccountLogo)
      );
    } else {
      return FileChunkingUtilities.splitAndUploadFile(
        file,
        (part, fileName, file) => this.uploadPart(part, fileName, file),
        (originalFileName, newFileName, isAttachment, isAccountLogo) =>
          this.completeUpload(originalFileName, newFileName, isAttachment, isAccountLogo),
        isAttachment,
        isAccountLogo
      );
    }
  }

  private compressAndSaveImage(imageName: string, image: string, isAttachment: boolean, isAccountLogo: boolean): Observable<string> {
    return new Observable<string>((observer: Observer<string>) => {
      void this.imageCompress.compressFile(image, null, 75, 75).then((res) => {
        FileChunkingUtilities.readAndUploadChunks(
          res,
          imageName,
          isAttachment,
          isAccountLogo,
          (part, fileName, file) => this.uploadPart(part, fileName, file),
          (originalFileName, newFileName, isAttachment, isAccountLogo) =>
            this.completeUpload(originalFileName, newFileName, isAttachment, isAccountLogo),
          observer
        );
      });
    });
  }

  public copyAttachments(copiedAttachmentsPayload: CopiedAttachmentsWhenLinkObjectViewModel): Observable<null> {
    return this.http.post<null>(`${this.actionUrl}CopyAttachments`, copiedAttachmentsPayload);
  }

  /**
   * Gets checklist documents grouped by checklist template for a specific object
   * @param objectId The ID of the object
   * @param objectType The type of the object
   * @returns Observable of checklist documents grouped by template
   */
  public getObjectChecklistDocuments(objectId: number, objectType: ObjectTypes): Observable<ChecklistDocumentViewModel[]> {
    return this.http.get<ChecklistDocumentViewModel[]>(`${this.actionUrl}ObjectChecklistDocuments/${objectId}/${objectType}`);
  }

}
